import { Fn as HotFn } from "hotscript";
import { Tuple } from "record-tuple";

import Symbols from "./Symbols.mts";
import UError from "./UError.mts";
import UTuple from "./UTuple.mts";

namespace UFunction {
  export type Maybe<Args extends any[], T> = ((...args: Args) => T) | T;
  export type Unwrap<T extends Maybe<any, any>> = T extends Any
    ? ReturnType<T>
    : T;

  export type Any = (...args: any) => any;
  export namespace Any {
    export type Void = (...args: any) => void;
    export type WithArgCountOf<Count extends number> = (
      ...args: UTuple.Of<any, Count>
    ) => any;
  }

  export type OptionalArgs<Fn extends Any> = (
    ...args: UTuple.Partial<Parameters<Fn>>
  ) => ReturnType<Fn>;

  export type CanReturnNullish<Fn extends Any> =
    Extract<ReturnType<Fn>, null | undefined> extends never ? false : true;

  export function is(maybeFn: any): maybeFn is Any {
    return (
      maybeFn && (maybeFn[Symbols.typeOf] || typeof maybeFn) === "function"
    );
  }

  const never = Symbol("never");
  export function once<Fn extends UFunction.Any>(fn: Fn) {
    let res = never;

    return ((...args) => {
      if (res === never) res = fn(...args);
      return res;
    }) as Fn;
  }

  export const immediate = <Fn extends UFunction.Any>(fn: Fn): ReturnType<Fn> =>
    fn();

  export function memo<Fn extends UFunction.Any>(
    fn: Fn,
    { by = Tuple.from as (args: Parameters<Fn>) => any } = {}
  ) {
    type Args = any[];
    type Result = any;
    // TODO: Use WeakMap here, holding arg references is fine when function is
    // ephemeral, but otherwise this memory will grow for long lived functions.
    // Impelment separate memo.deep that requires invalidation
    const cache = new Map<Args, Result>();

    return Object.assign(
      ((...args: Parameters<Fn>) => {
        const ref = by(args);

        if (!cache.has(ref)) cache.set(ref, fn(...args));

        return cache.get(ref);
      }) as Fn,
      // Pass through properties attached to fn
      fn,
      {
        cache:
          (...args: Parameters<Fn>) =>
          (value: ReturnType<Fn>) => {
            cache.set(by(args), value);
          },
        peek(...args: Parameters<Fn>) {
          return cache.get(by(args));
        },
        invalidate: Object.assign(
          (...args: Parameters<Fn>) => {
            return cache.delete(by(args));
          },
          {
            all() {
              cache.clear();
            },
          }
        ),
      }
    );
  }

  export const debounce = <Fn extends UFunction.Any>(fn: Fn, wait: number) => {
    // NodeJS.Timeout and number are not compatible, can't accurately type this
    let timeout: any;
    let pending: (() => void) | undefined;

    const res = Object.assign(
      (...args: Parameters<Fn>) => {
        clearTimeout(timeout);
        pending = () => {
          fn(...args);
          pending = undefined;
        };
        timeout = setTimeout(pending, wait);
      },
      // Pass through properties attached to fn
      fn,
      {
        immediate(...args: Parameters<Fn>): ReturnType<Fn> {
          res.clear();
          return fn(...args);
        },
        flush() {
          clearTimeout(timeout);
          pending?.();
        },
        clear() {
          clearTimeout(timeout);
          pending = undefined;
        },
      }
    );

    return res;
  };

  export const throttle = <Fn extends UFunction.Any>(
    fn: Fn,
    interval: number
  ) => {
    // NodeJS.Timeout and number are not compatible, can't accurately type this
    let timeout: any;
    let pending: (() => void) | undefined;

    return Object.assign(
      ((...args) => {
        pending = () => {
          fn(...args);
          pending = undefined;
          timeout = setTimeout(() => {
            timeout = undefined;
            pending?.();
          }, interval);
        };
        if (!timeout) pending();
      }) as Fn,
      {
        flush() {
          clearTimeout(timeout);
          timeout = undefined;
          pending?.();
        },
      }
    );
  };

  export const inferParams = <T, U extends T>(a: T): U => a as any;

  export const unwrap = <T, Args extends any[]>(
    fn: UFunction.Maybe<Args, T>,
    ...args: Args
  ): T => (typeof fn === "function" ? (fn as UFunction.Any)(...args) : fn);

  export const noop = () => {};
  export const identity = <T,>(value: T) => value;
  export const todo = () => {
    throw new UError.Unimplemented();
  };

  export const withArgs = <
    Args extends any[],
    Fn extends (...args: Args) => any,
  >(
    fn: Fn,
    ...args: Args
  ) => [fn, args];

  // This is only correct for functions without optional arguments, afaik
  // supporting optional arguments is not possible
  export type HasArgCountOf<
    Fn extends Any,
    Count extends number,
  > = HasArgCountOfImpl<Fn, UTuple.Of<any, Count>>;
  type HasArgCountOfImpl<Fn extends Any, Args extends any[]> = Fn extends (
    ...args: Args
  ) => any
    ? Args extends [any, ...infer Rest]
      ? // If Fn extends a function with a lower arg count, then it does not have
        // exactly `Count` args
        HasArgCountOfImpl<Fn, Rest> extends true
        ? false
        : true
      : true
    : false;

  export const hasArgCountOf = <Fn extends Any, Count extends number>(
    fn: Fn,
    count: Count
  ): fn is Fn extends any
    ? HasArgCountOf<Fn, Count> extends true
      ? Fn
      : never
    : never => fn.length === count;

  declare const hotFn: unique symbol;
  export const withHotFn =
    <TypeFn extends HotFn>() =>
    <Fn extends Any>(fn: Fn) =>
      fn as UFunction.WithTypeFn<Fn, TypeFn>;

  export type TypeFn<Fn extends Any & { [hotFn]?: HotFn }> = Fn extends {
    [hotFn]: infer TypeFn;
  }
    ? TypeFn
    : never;

  export type WithTypeFn<Fn extends Any, TypeFn extends HotFn> = Fn & {
    [hotFn]: TypeFn;
  };
}

export default UFunction;
